import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { ADMIN_ROUTES } from "@/routes";
import { usePageMeta } from "@/hooks/usePageMeta";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { TemplatePreviewDialog } from "@/components/admin/TemplatePreviewDialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  getTemplates,
  updateTemplate,
  uninstallTemplate,
  installTemplate,
  validateTemplatePackage,
  downloadStarterKit,
  exportTemplate,
  packageProblemsFromError,
  blobErrorMessage,
  type TemplateManagementItem,
  type TemplateValidationResult,
} from "@/services/api/templateManagement";
import { isTemplatePackage } from "@/lib/template-engine/resolve";
import type { TemplatePackage } from "@/lib/template-engine/types";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  FileArchive,
  Loader2,
  Package,
  ShieldCheck,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** The reference package the starter kit is built from (JUB-161). */
const STARTER_KEY = "golden_serenity";

// JUB-112: only golden_serenity and botez_bokeh survive as built-in, non-uninstallable designs.
const BUILTIN_TEMPLATES = new Set(["golden_serenity", "botez_bokeh"]);

function extractErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error;
  return response?.message || fallback;
}

function templateIdOf(template: TemplateManagementItem): number | undefined {
  return (template as { id?: number; templateId?: number }).id || (template as { id?: number; templateId?: number }).templateId;
}

function ProblemList({ problems, testId }: { problems: string[]; testId: string }) {
  return (
    <ul data-testid={testId} className="max-h-60 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-destructive">
      {problems.map((problem, index) => (
        <li key={index} className="break-words">
          {problem}
        </li>
      ))}
    </ul>
  );
}

export default function TemplateManagement() {
  const { t } = useTranslation();
  const _locale = useRouteLocale();
  const currentLocale = _locale || "ro";
  usePageMeta({
    title: t("admin.templates.metaTitle"),
    description: t("admin.templates.description"),
    locale: currentLocale,
    path: ADMIN_ROUTES.templates(currentLocale),
    noindex: true,
  });

  const [templates, setTemplates] = useState<TemplateManagementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<number | null>(null);
  const [uninstalling, setUninstalling] = useState<number | null>(null);
  const [exporting, setExporting] = useState<number | null>(null);
  const [downloadingStarter, setDownloadingStarter] = useState(false);
  const [preview, setPreview] = useState<{ pkg: TemplatePackage; title: string } | null>(null);
  const [uninstallDialog, setUninstallDialog] = useState<{ open: boolean; template: TemplateManagementItem | null }>({
    open: false,
    template: null,
  });
  const [installDialogOpen, setInstallDialogOpen] = useState(false);
  const [installFile, setInstallFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<TemplateValidationResult | null>(null);
  const [installProblems, setInstallProblems] = useState<string[]>([]);
  const [installing, setInstalling] = useState(false);
  const [installProgress, setInstallProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await getTemplates();
      setTemplates(data);
    } catch (error: unknown) {
      console.error("Failed to load templates:", error);
      toast.error(extractErrorMessage(error, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (template: TemplateManagementItem) => {
    try {
      const templateId = templateIdOf(template);
      if (!templateId) {
        toast.error(t("common.error"));
        return;
      }
      setUpdating(templateId);
      await updateTemplate(templateId, { active: !template.active });
      await loadTemplates();
      toast.success(template.active ? t("admin.templates.disabled") : t("admin.templates.enabled"));
    } catch (error: unknown) {
      console.error("Failed to update template:", error);
      toast.error(extractErrorMessage(error, t("common.error")));
    } finally {
      setUpdating(null);
    }
  };

  const handleUninstall = async () => {
    if (!uninstallDialog.template) return;

    try {
      const templateId = templateIdOf(uninstallDialog.template);
      if (!templateId) {
        toast.error(t("common.error"));
        return;
      }
      setUninstalling(templateId);
      await uninstallTemplate(templateId);
      await loadTemplates();
      toast.success(t("admin.templates.uninstalled"));
      setUninstallDialog({ open: false, template: null });
    } catch (error: unknown) {
      console.error("Failed to uninstall template:", error);
      toast.error(extractErrorMessage(error, t("common.error")));
    } finally {
      setUninstalling(null);
    }
  };

  const handleDownloadStarter = async () => {
    setDownloadingStarter(true);
    try {
      const { filename } = await downloadStarterKit(STARTER_KEY);
      toast.success(t("admin.templates.starter.downloaded", { file: filename }));
    } catch (error: unknown) {
      console.error("Failed to download starter kit:", error);
      toast.error((await blobErrorMessage(error)) ?? t("admin.templates.starter.error"));
    } finally {
      setDownloadingStarter(false);
    }
  };

  const handleExport = async (template: TemplateManagementItem) => {
    const templateId = templateIdOf(template);
    if (!templateId) return;
    setExporting(templateId);
    try {
      const { filename, omittedAssets } = await exportTemplate(templateId, template.key);
      if (omittedAssets > 0) {
        // Truthful: the archive is not the whole package.
        toast.warning(t("admin.templates.export.incomplete", { file: filename, count: omittedAssets }));
      } else {
        toast.success(t("admin.templates.export.done", { file: filename }));
      }
    } catch (error: unknown) {
      console.error("Failed to export template:", error);
      toast.error((await blobErrorMessage(error)) ?? t("admin.templates.export.error"));
    } finally {
      setExporting(null);
    }
  };

  const resetInstallState = () => {
    setInstallFile(null);
    setValidation(null);
    setInstallProblems([]);
    setInstallProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleInstallFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setValidation(null);
    setInstallProblems([]);
    if (file && !file.name.toLowerCase().endsWith(".zip") && file.type !== "application/zip") {
      toast.error(t("admin.templates.install.invalidFile"));
      e.target.value = "";
      setInstallFile(null);
      return;
    }
    setInstallFile(file);
  };

  const closeInstallDialog = () => {
    if (installing || validating) return;
    setInstallDialogOpen(false);
    resetInstallState();
  };

  const handleValidate = async () => {
    if (!installFile) return;
    setValidating(true);
    setInstallProblems([]);
    try {
      setValidation(await validateTemplatePackage(installFile));
    } catch (error: unknown) {
      console.error("Failed to validate template:", error);
      setValidation(null);
      toast.error(extractErrorMessage(error, t("admin.templates.validate.error")));
    } finally {
      setValidating(false);
    }
  };

  const handleInstall = async () => {
    if (!installFile || !validation?.valid) return;

    setInstalling(true);
    setInstallProgress(0);
    setInstallProblems([]);
    try {
      const installed = await installTemplate(installFile, setInstallProgress);
      await loadTemplates();
      toast.success(t("admin.templates.install.success", { name: installed.name }));
      setInstallDialogOpen(false);
      resetInstallState();
    } catch (error: unknown) {
      console.error("Failed to install template:", error);
      // Show every problem the server reported, not just a summary.
      setInstallProblems(packageProblemsFromError(error));
      toast.error(extractErrorMessage(error, t("admin.templates.install.error")));
    } finally {
      setInstalling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader currentLocale={currentLocale} active="templates" />
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader currentLocale={currentLocale} active="templates" />
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t("admin.templates.title")}</h1>
            <p className="text-muted-foreground">{t("admin.templates.description")}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t("admin.templates.starter.hint")}</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row md:shrink-0">
            <Button variant="outline" onClick={handleDownloadStarter} disabled={downloadingStarter} data-testid="template-starter-download">
              {downloadingStarter ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {t("admin.templates.starter.button")}
            </Button>
            <Button onClick={() => setInstallDialogOpen(true)} data-testid="template-install-open">
              <Upload className="h-4 w-4 mr-2" />
              {t("admin.templates.install.button")}
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => {
            const isBuiltin = BUILTIN_TEMPLATES.has(template.key);
            const templateId = templateIdOf(template);
            const isUpdating = updating === templateId;
            const pkg = isTemplatePackage(template.config) ? template.config : null;
            const displayName = isBuiltin
              ? t(`events.form.templateCatalog.${template.key}.name`, { defaultValue: template.name })
              : pkg?.manifest.name || template.name;
            const displayDescription = isBuiltin
              ? t(`events.form.templateCatalog.${template.key}.tagline`, {
                  defaultValue: template.description || template.key,
                })
              : template.description || pkg?.manifest.description || template.key;

            return (
              <Card key={template.key} className="relative" data-testid={`template-card-${template.key}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-xl break-words">{displayName}</CardTitle>
                      <CardDescription className="mt-1 break-words">{displayDescription}</CardDescription>
                      <p className="mt-1 font-mono text-xs text-muted-foreground break-all">{template.key}</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      {template.premium && <Badge variant="secondary">{t("admin.templates.badges.premium")}</Badge>}
                      {template.active ? (
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {t("admin.templates.badges.active")}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="h-3 w-3 mr-1" />
                          {t("admin.templates.badges.inactive")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor={`toggle-${template.key}`} className="text-sm">
                        {t("admin.templates.enable")}
                      </Label>
                      <Switch
                        id={`toggle-${template.key}`}
                        checked={template.active}
                        onCheckedChange={() => handleToggleActive(template)}
                        disabled={isUpdating}
                      />
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        disabled={!pkg}
                        title={pkg ? undefined : t("admin.templates.preview.unavailable")}
                        onClick={() => pkg && setPreview({ pkg, title: displayName })}
                        data-testid={`template-preview-${template.key}`}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {t("common.preview")}
                      </Button>
                      {pkg && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          disabled={exporting === templateId}
                          onClick={() => handleExport(template)}
                          data-testid={`template-export-${template.key}`}
                        >
                          {exporting === templateId ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <FileArchive className="h-4 w-4 mr-2" />
                          )}
                          {t("admin.templates.export.button")}
                        </Button>
                      )}
                      {!isBuiltin && (
                        <Button
                          variant="destructive"
                          size="sm"
                          aria-label={t("admin.templates.uninstall")}
                          onClick={() => setUninstallDialog({ open: true, template })}
                          data-testid={`template-uninstall-${template.key}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {!pkg && <p className="text-xs text-muted-foreground">{t("admin.templates.preview.unavailable")}</p>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {templates.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">{t("admin.templates.noTemplates")}</p>
            </CardContent>
          </Card>
        )}

        {/* Uninstall Dialog */}
        <AlertDialog open={uninstallDialog.open} onOpenChange={(open) => setUninstallDialog({ open, template: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("admin.templates.uninstallConfirm")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("admin.templates.uninstallWarning")}
                {uninstallDialog.template && (
                  <span className="block mt-2 font-semibold">{uninstallDialog.template.name}</span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleUninstall}
                disabled={uninstalling !== null}
                data-testid="template-uninstall-confirm"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {uninstalling !== null ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("admin.templates.uninstalling")}
                  </>
                ) : (
                  t("admin.templates.uninstall")
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Install Dialog: choose → validate → preview → install */}
        <Dialog open={installDialogOpen} onOpenChange={(open) => (open ? setInstallDialogOpen(true) : closeInstallDialog())}>
          <DialogContent className="w-[min(94vw,40rem)] max-w-none max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("admin.templates.install.title")}</DialogTitle>
              <DialogDescription>{t("admin.templates.install.description")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,application/zip"
                onChange={handleInstallFileChange}
                disabled={installing || validating}
                data-testid="template-install-file"
                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-sm file:font-medium file:text-secondary-foreground hover:file:bg-secondary/80"
              />

              {validation && !validation.valid && (
                <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3" data-testid="template-validation-invalid">
                  <p className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <XCircle className="h-4 w-4 shrink-0" />
                    {t("admin.templates.validate.invalid", { count: validation.problems.length })}
                  </p>
                  <ProblemList problems={validation.problems} testId="template-validation-problems" />
                </div>
              )}

              {validation?.valid && (
                <div className="space-y-2 rounded-md border border-green-600/40 bg-green-50 p-3 text-sm dark:bg-green-950/30" data-testid="template-validation-valid">
                  <p className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
                    <ShieldCheck className="h-4 w-4 shrink-0" />
                    {t("admin.templates.validate.valid")}
                  </p>
                  <p className="break-words">
                    {t("admin.templates.validate.summary", { name: validation.name ?? "", key: validation.key ?? "" })}
                  </p>
                  <p className="text-muted-foreground">
                    {validation.install?.action === "replace_disabled"
                      ? t("admin.templates.validate.actionReplace")
                      : t("admin.templates.validate.actionCreate")}
                  </p>
                  {validation.omittedAssets.length > 0 && (
                    <div className="text-amber-700 dark:text-amber-400" data-testid="template-validation-omitted">
                      <p className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        {t("admin.templates.validate.omittedAssets", { count: validation.omittedAssets.length })}
                      </p>
                      <ul className="mt-1 list-disc pl-5 font-mono text-xs">
                        {validation.omittedAssets.map((asset) => (
                          <li key={asset.path} className="break-all">
                            {asset.path}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {validation.ignoredFiles.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t("admin.templates.validate.ignoredFiles", { count: validation.ignoredFiles.length })}
                    </p>
                  )}
                </div>
              )}

              {installProblems.length > 0 && (
                <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3" data-testid="template-install-problems">
                  <p className="text-sm font-medium text-destructive">{t("admin.templates.install.error")}</p>
                  <ProblemList problems={installProblems} testId="template-install-problem-list" />
                </div>
              )}

              {installing && (
                <div className="space-y-1">
                  <Progress value={installProgress} />
                  <p className="text-xs text-muted-foreground text-right">{installProgress}%</p>
                </div>
              )}

              {!validation?.valid && <p className="text-xs text-muted-foreground">{t("admin.templates.install.mustValidate")}</p>}
            </div>

            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={closeInstallDialog} disabled={installing || validating}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="outline"
                onClick={handleValidate}
                disabled={!installFile || validating || installing}
                data-testid="template-validate"
              >
                {validating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("admin.templates.validate.validating")}
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    {t("admin.templates.validate.button")}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  validation?.package && setPreview({ pkg: validation.package, title: validation.name ?? validation.key ?? "" })
                }
                disabled={!validation?.valid || !validation.package || installing}
                data-testid="template-validated-preview"
              >
                <Eye className="h-4 w-4 mr-2" />
                {t("common.preview")}
              </Button>
              <Button onClick={handleInstall} disabled={!validation?.valid || installing} data-testid="template-install-confirm">
                {installing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("admin.templates.install.installing")}
                  </>
                ) : (
                  t("admin.templates.install.confirm")
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <TemplatePreviewDialog
          open={preview !== null}
          onOpenChange={(open) => !open && setPreview(null)}
          pkg={preview?.pkg ?? null}
          title={preview?.title ?? ""}
        />
      </div>
    </div>
  );
}
